/**
 * Dashboard — read model for the `/dashboard` surface (Phase 3, turn 12.1).
 *
 * Dashboard is a read-only aggregator: every card is a window into another
 * feature's data. The VM (`useDashboardVM`) fetches one aggregate payload —
 * the `get_dashboard(p_org_id)` RPC — and normalizes the snake_case rows into
 * the camelCase shapes below. The populated-mode card stack (turn 12.2)
 * composes primitives over these arrays; onboarding mode (turn 12.3) is a
 * separate branch keyed off `mode`.
 *
 * The backing tables (`documents`, `tasks`, the `org_events` activity source)
 * land in Phase 3's schema turns (13.1 / 14.1 / 15.1) and the `get_dashboard`
 * RPC on `penrose-supabase` alongside them. Until then the VM treats a
 * "function does not exist" response as an empty populated dashboard so the
 * surface still renders — see `useDashboardVM`.
 */

/** Activity feed hard cap — 20 events, per the dashboard prd. */
export const ACTIVITY_CAP = 20;

/** Per-card row cap for Pending work + Recent documents, per the prd. */
export const CARD_ROW_CAP = 10;

/**
 * Dashboard render mode. `populated` is the standard card stack; `onboarding`
 * is the fresh-org 5-step checklist (built in turn 12.3). The mode is decided
 * server-side per render so the user never sees a flicker between the two.
 */
export type DashboardMode = 'populated' | 'onboarding';

/** A Pending-work row: a task assigned to me, or a doc where I'm DRI/owner. */
export interface PendingWorkItem {
  id: string;
  kind: 'task' | 'doc';
  title: string;
  /** Document type label — `doc` rows only; `null` for tasks. */
  docType: string | null;
  /** Status label (task status, or doc status). */
  status: string;
  /** ISO timestamp of the most recent event on the entity. */
  lastEventAt: string;
  /** ISO due date — `task` rows that carry one; `null` otherwise. */
  dueAt: string | null;
}

/** A Recent-documents row: a doc touched in the last 30 days I can read. */
export interface RecentDocItem {
  id: string;
  docType: string;
  title: string;
  status: string;
  /** Display name of the actor whose event surfaced this row. */
  actorName: string;
  lastEventAt: string;
}

/** Which detail surface an activity event clicks through to. */
export type ActivityKind = 'doc' | 'task' | 'member';

/** One Activity-feed event. */
export interface ActivityEvent {
  id: string;
  actorName: string;
  /** True when the actor is a now-archived member — renders a muted badge. */
  actorArchived: boolean;
  /** Verb phrase: `created`, `edited`, `changed status of`, `assigned`, … */
  verb: string;
  /** Subject type label (doc/task type). */
  subjectType: string;
  subjectTitle: string;
  /** Id of the entity the row routes to. */
  subjectId: string;
  kind: ActivityKind;
  occurredAt: string;
}

/** A pending invitation matched to the viewer's email. */
export interface PendingInvitationItem {
  /** `invitations.id`. */
  id: string;
  /** `invitations.token` — passed to `accept_invitation(p_token)` from the
   *  dashboard's `<InvitationRow>` Accept button (17.3b.2). When the
   *  `get_dashboard` RPC has not been amended yet to emit `token` the
   *  normalizer leaves this as the empty string and the Accept button
   *  no-ops; the flyout's pending-invitations section bypasses this model
   *  and uses the org-switcher VM's `PendingInvitation` (which always
   *  carries the token) so the user-facing accept path still works. */
  token: string;
  orgName: string;
  /** Permission/function role label the viewer would join as. */
  roleLabel: string;
  inviterName: string;
  invitedAt: string;
}

/** The full normalized dashboard payload the VM exposes. */
export interface DashboardData {
  mode: DashboardMode;
  pendingWork: PendingWorkItem[];
  recentDocs: RecentDocItem[];
  activity: ActivityEvent[];
  pendingInvitations: PendingInvitationItem[];
}

/** True when a `task`-kind item carries a due date already in the past. */
export function isOverdue(
  item: PendingWorkItem,
  now: number = Date.now()
): boolean {
  if (!item.dueAt) return false;
  const ts = Date.parse(item.dueAt);
  return Number.isFinite(ts) && ts < now;
}

/**
 * Pending-work ordering, per the prd: overdue items first, then by
 * `lastEventAt` descending. Pure — returns a new array.
 */
export function sortPendingWork(
  items: readonly PendingWorkItem[],
  now: number = Date.now()
): PendingWorkItem[] {
  return [...items].sort((a, b) => {
    const aOver = isOverdue(a, now);
    const bOver = isOverdue(b, now);
    if (aOver !== bOver) return aOver ? -1 : 1;
    return Date.parse(b.lastEventAt) - Date.parse(a.lastEventAt);
  });
}

/** Recent-documents ordering: `lastEventAt` descending. Pure. */
export function sortRecentDocs(
  items: readonly RecentDocItem[]
): RecentDocItem[] {
  return [...items].sort(
    (a, b) => Date.parse(b.lastEventAt) - Date.parse(a.lastEventAt)
  );
}

/** A `<Pill>` status variant. */
export type PillStatus =
  | 'todo'
  | 'progress'
  | 'review'
  | 'blocked'
  | 'done'
  | 'cancel'
  | 'draft'
  | 'published';

/**
 * Map a free-text status label (task status or doc status, as the
 * `get_dashboard` RPC returns it) onto a `<Pill>` status variant. Unknown
 * labels fall back to `todo`. Pure.
 */
export function pillStatus(raw: string): PillStatus {
  const k = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  switch (k) {
    case 'in_progress':
    case 'progress':
      return 'progress';
    case 'in_review':
    case 'review':
      return 'review';
    case 'blocked':
      return 'blocked';
    case 'done':
    case 'complete':
    case 'completed':
      return 'done';
    case 'cancel':
    case 'cancelled':
    case 'canceled':
      return 'cancel';
    case 'draft':
      return 'draft';
    case 'published':
      return 'published';
    case 'archived':
      return 'cancel';
    case 'ready':
      return 'review';
    default:
      return 'todo';
  }
}

/** Two-letter initials for an actor avatar — "Lane Fries" → "LF". */
export function actorInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((part) => part[0] ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}
